/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */
import {EventEmitter, Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import find from 'lodash-es/find';
import {BehaviorSubject, forkJoin, Observable, of} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';

import {REQUEST_OPTIONS} from '../../app.constants';
import {PayPeriod, PayPeriodAttributes} from '../../models/models.interfaces';
import {ApiList, ApiRequestOptions, ApiService, Data} from '../../shared/api';
import {RolesService} from '../../shared/authentication/roles/roles.service';
import {DefaultTransIdSort, SortUtilsService} from '../../shared/sort/sort-utils.service';
import {SortDirection} from '../../shared/ui/table/table.constants';
import {SortExpression} from '../../shared/ui/table/table.interfaces';
import {Headers} from '../../shared/utils/headers';
import {Payrolls} from '../../shared/utils/payrolls';
import {QueryParamBuilder} from '../../shared/utils/query-param-builder';
import {CalculationType, PayType, PayTypeWithCliRegIds} from '../payrolls.model';

import {
  OngoingPayrollsResponse,
  OngoingPayrollStatistics,
  Pay,
  PayRequest,
  PayResponse,
  PayrollStatus,
  PayrollStatusData,
  PaySerie,
  PayStatusDetail
} from './ongoing-payrolls.interfaces';

/*const accumulateFinishedPayrolls = (count: number, detail: PayStatusDetail) =>
  count + ['FROZEN', 'DONE'].indexOf(detail.type) >= 0 ? detail.count : 0;*/

/**
 * Ongoing payrolls service that helps fetching payrolls and payrolls statistics, and execute pay actions
 */
@Injectable()
export class OngoingPayrollsService {

  public isPayrollLaunched: EventEmitter<number> = new EventEmitter<number>();
  private donePayrollCount: number = 0;
  private frozenPayrollCount: number = 0;
  private lastExecutionIdSubject: BehaviorSubject<number> = new BehaviorSubject<number>(null);
  public getLastExecutionId = this.lastExecutionIdSubject.asObservable();

  constructor(private api: ApiService,
              private translate: TranslateService,
              private rolesService: RolesService,
              private sortUtilsService: SortUtilsService) {
  }

  public listOngoingPayPeriods(): Observable<Array<Data<PayPeriodAttributes>>> {
    return this.api.get(`/payroll/api/v1/payPeriod/ongoing?onlyPureOngoingAndFuture=false`)
      .pipe(map(response => response.data));
  }

  public getPayPeriodById(payPeriodId: number): Observable<PayPeriod> {
    return this.api.get(`/payroll/api/v1/pay-period/${payPeriodId}`);
  }

  public listAllPayPeriodContract(opts: ApiRequestOptions): Observable<Pay[]> {
    return this.api.get(`/payroll/api/v1/pay-period-contract`, opts)
      .pipe(map(response => response.body));
  }

  /**
   * Fetch payloads
   *
   * @param perPage how many per page
   * @param page which page to request
   * @param search the FIQL search string
   * @param orderBy
   * @returns {Observable<OngoingPayrollsResponse>} observable that emits the response
   */
  public fetchPayrolls(perPage: number,
                       page: number,
                       search: string = '',
                       orderBy: string): Observable<OngoingPayrollsResponse> {

    const opts: ApiRequestOptions = REQUEST_OPTIONS.OBSERVE.RESPONSE;
    opts.requestOptions.params = {perPage, page, orderBy};
    if (search) {
      opts.requestOptions.params.search = search;
    }

    return this.api.get('/payroll/api/v1/ongoing', opts)
      .pipe(
        map((response: any) => {
          if (response.status === 204) {
            return <OngoingPayrollsResponse>{
              payload: <PayResponse>{
                datas: [],
                buttonAllInfos: {
                  total: 0,
                  detail: []
                }
              },
              total: 0
            };
          }

          return <OngoingPayrollsResponse>{
            payload: <PayResponse>response.body,
            total: parseInt(Headers.get(response.headers, 'X-Total-Count'), 10)
          };
        })
      );
  }

  public getFinishedPayrollsCount(details: Array<PayStatusDetail>): number {
    const donePayrollsObj: any = find(details, ['type', 'DONE']);
    const frozenPayrollsObj: any = find(details, ['type', 'FROZEN']);
    if (frozenPayrollsObj !== undefined) {
      this.frozenPayrollCount = frozenPayrollsObj.count;
    }
    if (donePayrollsObj !== undefined) {
      this.donePayrollCount = donePayrollsObj.count;
    }
    return this.donePayrollCount + this.frozenPayrollCount;
  }

  /**
   * Fetch reporting statistics and prepare data
   */
  public getStatistics(): Observable<OngoingPayrollStatistics> {
    if (!this.rolesService.hasRole('PAYROLL_READ', 'PAYROLL_WRITE')) {
      return of(null);
    }

    return forkJoin([
      this.getPayrollStatistics(),
      this.getPayRequests(),
      this.getPayrollStatus()
    ])
      .pipe(
        map((responses: any[]) => {
          if (!responses) {
            return null;
          }

          // Pay series
          const paySeries: Array<PaySerie> = responses[0];

          // Pay requests
          const payRequests = <ApiList<Array<PayRequest>>>responses[1];
          const latestRequests: Array<PayRequest> = payRequests.data.slice(0, 5);
          const ongoingCalculations: number =
            payRequests.data.filter((payRequest: PayRequest) => payRequest.attributes.progress > 0).length;
          const latestFinishedRequest: PayRequest =
            payRequests.data.find((payRequest: PayRequest) => payRequest.attributes.progress === 100);

          // Payroll status
          const payrollStatusData: { data: PayrollStatus } = responses[2];
          const payrollStatus: PayrollStatus = <PayrollStatus>payrollStatusData.data;
          const contractsManaged: number = payrollStatusData.data.total;

          const finishedPayrolls: number = this.getFinishedPayrollsCount(payrollStatus.detail);
          const ongoingPayrolls: number = payrollStatus.detail.length;

          return <OngoingPayrollStatistics>{
            payrollStatus,
            latestRequests,
            latestFinishedRequest,
            ongoingPayrolls,
            ongoingCalculations,
            contractsManaged,
            paySeries,
            finishedPayrolls
          };
        })
      );
  }

  private getPayRequests(): Observable<ApiList<Array<PayRequest>>> {
    return this.api.get('/payroll/api/v1/payRequest?orderBy=-updatedate&perPage=10');
  }

  public getPayrollStatistics(): Observable<Array<PaySerie>> {
    return this.api.get('/payroll/api/v1/reporting/paySerieStatistics');
  }

  public getPayrollStatus(): Observable<{ data: PayrollStatus }> {
    return this.api.get('/payroll/api/v1/reporting/payrollStatus');
  }

  public getPayrollStatusChart(statistics: OngoingPayrollStatistics | PayrollStatusData): any {
    const chartData = statistics.payrollStatus.detail.map(detail => {
      return {
        value: detail.count,
        name: Payrolls.getDisplayNameForStatus(detail.type, this.translate),
        color: Payrolls.getColorForStatus(detail.type),
        key: detail.type
      };
    });

    if (chartData.length === 0) {
      return;
    }

    const colors = chartData.map(data => data.color),
      legend = chartData.map(data => data.name);

    return {
      tooltip: {
        trigger: 'item',
        formatter: '{a} <br/>{b} : {c} ({d}%)'
      },
      legend: {
        orient: 'horizontal',
        y: 'bottom',
        data: legend
      },
      color: colors,
      calculable: true,
      series: [{
        name: this.translate.instant('COMMON.payrolls'),
        type: 'pie',
        radius: ['50%', '70%'],
        center: ['50%', '43%'],
        data: chartData,
        itemStyle: {
          normal: {
            label: {
              formatter: '{c}',
              distance: 0.1
            }
          }
        }
      }]
    };
  }

  /**
   * Launch pays with passed ids
   * @param ids
   * @param isRetroactivity
   * @param isDebugging
   * @param date
   * @param priority
   */
  public launch(ids: Array<number>,
                isRetroactivity: boolean = false,
                isDebugging: boolean = false,
                date?: string,
                priority?: 'HIGH' | 'MEDIUM' | 'LOW') {
    return this.api.post(
      new QueryParamBuilder('/payroll/api/v1/ongoing/launch')
        .add('isDebug', isDebugging)
        .addIfNotNull('recallUntilThisDate', date, isRetroactivity)
        .addIfNotNull('priority', priority)
        .build(), {
        ids
      });
  }

  /**
   * Launch all pays with passed FIQL.
   * @param fiql
   * @param isRetroactivity
   * @param isDebugging
   * @param date
   * @param priority
   */
  public launchAll(fiql: string,
                   isRetroactivity: boolean = false,
                   isDebugging: boolean = false,
                   date?: string,
                   priority?: 'HIGH' | 'MEDIUM' | 'LOW') {
    return this.api.post(
      new QueryParamBuilder('/payroll/api/v1/ongoing/launchAll')
        .addIfNotNull('search', fiql)
        .add('isDebug', isDebugging)
        .addIfNotNull('recallUntilThisDate', date, isRetroactivity)
        .addIfNotNull('priority', priority)
        .build()
    );
  }

  /**
   * Freeze (fix) all pays with passed FIQL.
   */
  public freezeAll(fiql: string) {
    return this.api.post(
      new QueryParamBuilder('/payroll/api/v1/ongoing/fixAll')
        .addIfNotNull('search', fiql)
        .build());
  }

  /**
   * Unfreeze all pays with passed FIQL.
   */
  public unfreezeAll(fiql: string) {
    return this.api.post(
      new QueryParamBuilder('/payroll/api/v1/ongoing/unfreezeAll')
        .addIfNotNull('search', fiql)
        .build());
  }

  /**
   * Done all pays with passed FIQL.
   */
  public doneAll(fiql: string) {
    return this.api.post(
      new QueryParamBuilder('/payroll/api/v1/ongoing/doneAll')
        .addIfNotNull('search', fiql)
        .build());
  }

  /**
   * Change status for payrolls
   *
   * @returns {Observable<Response>}
   */
  public changeStatus(doneIds: number[],
                      frozenIds: number[],
                      unfrozenIds: number[],
                      lastDoneIds: number[],
                      readyIds: number[],
                      nextIds: number[]) {
    return this.api.post('/payroll/api/v1/ongoing/status', {
      doneIds, frozenIds, unfrozenIds, lastDoneIds, readyIds, nextIds
    });
  }

  public createSortExpression(key: string): SortExpression {
    let direction;

    if (key[0] === '-') {
      key = key.substr(1);
      direction = SortDirection.DESCENDING;
    } else {
      direction = SortDirection.ASCENDING;
    }

    return {key, direction};
  }

  public updateLastExecutionId(id: number) {
    this.lastExecutionIdSubject.next(id);
  }

  public fetchPayTypes(id?: number, status?: string): Observable<PayType[]> {
    const condition = !!id && !!status;
    return this.api.get(new QueryParamBuilder('/payroll/api/v1/payPeriod/pay-types')
      .add('pay-period-id', id, condition)
      .add('status', status, condition)
      .build());
  }

  // Subscribe to PayrollUtilsService payTypesSorted$
  // in order to get distinct and sorted pay types both on init and when lang changes
  public fetchDistinctPayTypes(): Observable<PayTypeWithCliRegIds[]> {
    return this.api.get('/payroll/api/v1/payPeriod/pay-types')
      .pipe(
        map((payTypes: PayType[]) => this.distinctPayTypesByNameAndTransId(payTypes)),
        switchMap((payTypes: PayTypeWithCliRegIds[]) =>
          this.sortUtilsService.getObservableSortByTransId(payTypes, 'transId', new PayTypeTransIdSort()))
      );
  }

  // When lang has changed, we keep existing list but we need new translations and sort
  public updatePayTypesOnLangChange(payTypes: PayTypeWithCliRegIds[]): Observable<PayTypeWithCliRegIds[]> {
    return this.sortUtilsService.getObservableSortByTransId(payTypes, 'transId', new PayTypeTransIdSort());
  }

  // distinct pay types by name and trans id as a pay type is linked to one regulation
  public distinctPayTypesByNameAndTransId(payTypes: PayType[]): PayTypeWithCliRegIds[] {
    if (!payTypes || payTypes.length <= 0) {
      return [];
    }
    return payTypes.reduce((filteredPayTypes: PayTypeWithCliRegIds[], payType: PayType) => {
      const foundPayType = filteredPayTypes
        .find(findPayType => findPayType.name === payType.name && findPayType.transId === payType.transId);
      if (!foundPayType) {
        const newPayType: PayTypeWithCliRegIds = {
          cliRegIds: [payType.cliRegId],
          allowLaunchAfterClose: payType.allowLaunchAfterClose,
          calculationType: payType.calculationType,
          name: payType.name,
          transId: payType.transId
        };
        filteredPayTypes.push(newPayType);
      } else {
        foundPayType.cliRegIds.push(payType.cliRegId);
      }
      return filteredPayTypes;
    }, []);
  }
}

// NORMAL calculationType first, then by asc
class PayTypeTransIdSort extends DefaultTransIdSort<PayTypeWithCliRegIds> {
  sort = (transIdPropertyName: string, transValues): (p1: PayTypeWithCliRegIds, p2: PayTypeWithCliRegIds) => number => {
    return (payTypeA, payTypeB) => {
      if ((payTypeA.calculationType === payTypeB.calculationType)) {
        return super.defaultSort(payTypeA, payTypeB, transIdPropertyName, transValues);
      } else {
        return payTypeA.calculationType === CalculationType.NORMAL ? -1 : 1;
      }
    };
  }
}
